import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Select,
  VStack,
  HStack,
  Spacer,
  Input,
} from "@chakra-ui/react";
import DataTable from "react-data-table-component";
import { tableCustomStyles } from "../../themes/TableStyles";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import {
  Search,
  ExportExcel,
  ExportPdf,
  Print,
  Clear,
} from "../../themes/IconButtonSet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTable } from "@fortawesome/free-solid-svg-icons";
import VaccinePreventionDemandPlanChart from "./VaccinePreventionDemandPlanChart";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

// ฟังก์ชันโหลดฟอนต์ Sarabun
const loadSarabunFont = async () => {
  try {
    const fontResponse = await fetch("/fonts/Sarabun-Regular.ttf");
    if (!fontResponse.ok) {
      throw new Error(
        `Failed to fetch Sarabun font: ${fontResponse.statusText}`
      );
    }
    const fontArrayBuffer = await fontResponse.arrayBuffer();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result
          .replace("data:application/octet-stream;base64,", "")
          .replace("data:font/ttf;base64,", "");
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(new Blob([fontArrayBuffer]));
    });
  } catch (error) {
    console.error("Error loading Sarabun font:", error);
    return null;
  }
};

const VaccinePreventionDemandPlanReport = () => {
  const { sessionId } = useAuth();
  const [filterData, setFilterData] = useState({
    EstimateYear: "",
    EstimateUnit: "",
    LivestockType: "",
    VaccineNameTH: "",
  });
  const [unitOptions, setUnitOptions] = useState([]);
  const [livestockTypeOptions, setLivestockTypeOptions] = useState([]);
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [demandData, setDemandData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);



  // ดึงข้อมูล DLDControl Sub-unit List
  const fetchUnitOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getDLDControlSubUnitList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setUnitOptions(response.UnitList || []);
      } else {
        console.error("Failed to fetch units:", response.error);
      }
    } catch (error) {
      console.error("Error fetching units:", error);
    }
  };


  // ดึงข้อมูล Livestock Type List
  const fetchLivestockTypeOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getLivestockTypeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const types = response.LivestockTypeList || [];
        setLivestockTypeOptions(types);
        if (types.length === 0) {
          console.warn("LivestockTypeList is empty");
        }
      } else {
        console.error("Failed to fetch livestock types:", response.error);
      }
    } catch (error) {
      console.error("Error fetching livestock types:", error);
    }
  };

  // ดึงข้อมูล Vaccine List
  const fetchVaccineOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setVaccineOptions(response.VaccinesList || []);
      } else {
        console.error("Failed to fetch vaccines:", response.error);
      }
    } catch (error) {
      console.error("Error fetching vaccines:", error);
    }
  };

  // เรียก API getVaccinePreventionDemandPlanSummary
  const fetchDemandPlanReport = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinePreventionDemandPlanSummary",
        data: {
          EstimateYear: filterData.EstimateYear || "",
          EstimateUnit: filterData.EstimateUnit || "",
          LivestockType: filterData.LivestockType || "",
          VaccineNameTH: filterData.VaccineNameTH || "",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setDemandData(response.summary || []);
      } else {
        setDemandData([]);
        alert("ไม่พบข้อมูล: " + response.error);
      }
    } catch (error) {
      console.error("Error fetching demand plan report:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchUnitOptions();
      fetchLivestockTypeOptions();
      fetchVaccineOptions();
    }
  }, [sessionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData({ ...filterData, [name]: value });
  };

  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ปีงบประมาณ</span>
        </HStack>
      ),
      selector: (row) => row.EstimateYear,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>หน่วยงาน</span>
        </HStack>
      ),
      selector: (row) => row.EstimateUnit,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ประเภทปศุสัตว์</span>
        </HStack>
      ),
      selector: (row) => row.LivestockType,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ชื่อวัคซีน (TH)</span>
        </HStack>
      ),
      selector: (row) => row.VaccineNameTH,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ชื่อวัคซีน (EN)</span>
        </HStack>
      ),
      selector: (row) => row.VaccineNameEN,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนความต้องการ</span>
        </HStack>
      ),
      selector: (row) => Number(row.TotalQuantityDemanded)?.toLocaleString(),
      sortable: true,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>มูลค่าความต้องการ</span>
        </HStack>
      ),
      selector: (row) => Number(row.TotalValueDemanded)?.toLocaleString(),
      sortable: true,
      width: "200px",
    },
  ];

  const exportToExcel = () => {
    const wsData = demandData.map((item) => ({
      "ปีงบประมาณ": item.EstimateYear,
      "หน่วยงาน": item.EstimateUnit,
      "ประเภทปศุสัตว์": item.LivestockType,
      "ชื่อวัคซีน (TH)": item.VaccineNameTH,
      "ชื่อวัคซีน (EN)": item.VaccineNameEN,
      "จำนวนความต้องการ": item.TotalQuantityDemanded,
      "มูลค่าความต้องการ": item.TotalValueDemanded,
    }));
    const ws = XLSX.utils.json_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "DemandPlanReport");
    XLSX.writeFile(wb, "VaccinePreventionDemandPlanReport.xlsx");
  };

  const exportToPDF = async () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const fontBase64 = await loadSarabunFont();
    if (fontBase64) {
      doc.addFileToVFS("Sarabun.ttf", fontBase64);
      doc.addFont("Sarabun.ttf", "Sarabun", "normal");
      doc.setFont("Sarabun");
    } else {
      console.warn("Failed to load Sarabun font, using default font");
    }

    doc.setFontSize(16);
    doc.text("รายงานแผนความต้องการวัคซีน", 14, 10);
    doc.setFontSize(10);
    doc.text(`วันที่: ${new Date().toLocaleDateString("th-TH")}`, 14, 18);

    autoTable(doc, {
      startY: 25,
      head: [
        [
          "ปีงบประมาณ",
          "หน่วยงาน",
          "ประเภทปศุสัตว์",
          "ชื่อวัคซีน (TH)",
          "ชื่อวัคซีน (EN)",
          "จำนวนความต้องการ",
          "มูลค่าความต้องการ",
        ],
      ],
      body: demandData.map((item) => [
        item.EstimateYear,
        item.EstimateUnit,
        item.LivestockType,
        item.VaccineNameTH,
        item.VaccineNameEN,
        item.TotalQuantityDemanded,
        item.TotalValueDemanded,
      ]),
      styles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontSize: 12,
        cellPadding: 2,
      },
      headStyles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontStyle: "normal",
        fillColor: [22, 160, 133],
        textColor: [255, 255, 255],
      },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 60 },
        2: { cellWidth: 50 },
        3: { cellWidth: 40 },
        4: { cellWidth: 60 },
        5: { cellWidth: 60 },
        6: { cellWidth: 40 },
        7: { cellWidth: 40 },
      },
      didParseCell: (data) => {
        data.cell.styles.cellWidth = "auto";
      },
    });

    doc.save("VaccinePreventionDemandPlanReport.pdf");
  };

  const printTable = () => {
    const printContent = `
      <html>
        <head>
          <title>Vaccine Demand Plan Report</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <h2>Vaccine Demand Plan Report</h2>
          <table>
            <thead>
              <tr>
                <th>ปีงบประมาณ</th>
                <th>หน่วยงาน</th>
                <th>ประเภทปศุสัตว์</th>
                <th>ชื่อวัคซีน (TH)</th>
                <th>ชื่อวัคซีน (EN)</th>
                <th>จำนวนความต้องการ</th>
                <th>มูลค่าความต้องการ</th>
              </tr>
            </thead>
            <tbody>
              ${demandData
                .map(
                  (item) => `
                    <tr>
                      <td>${item.EstimateYear}</td>
                      <td>${item.EstimateUnit}</td>
                      <td>${item.LivestockType}</td>
                      <td>${item.VaccineNameTH}</td>
                      <td>${item.VaccineNameEN}</td>
                      <td>${item.TotalQuantityDemanded}</td>
                      <td>${item.TotalValueDemanded}</td>
                    </tr>
                  `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;
    const newWindow = window.open("", "", "width=800,height=600");
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.print();
  };

  return (
    <Box p={35} m={25} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <Heading as="h2" mb={10} textAlign="center">
        รายงานแผนความต้องการวัคซีน
      </Heading>

      {/* ฟอร์มฟิลเตอร์ */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <Box
          mb={5}
          borderRadius="full"
          w="100%"
          bgColor="iBlue.600"
          color="white"
          p={2}
        >
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faSearch} color="white" size="xl" />
            <Heading size="lg" fontWeight="normal">
              ค้นหาข้อมูลแผนความต้องการ
            </Heading>
          </HStack>
        </Box>
        <VStack spacing={6} p={5}>
          <HStack spacing={6} w="100%" flexWrap="wrap">
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                ปีงบประมาณ
              </FormLabel>
              <Input
                type="number"
                name="EstimateYear"
                value={filterData.EstimateYear}
                onChange={handleInputChange}
                placeholder="กรอกปีงบประมาณ (ถ้ามี)"
                colorScheme="iBlue"
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                หน่วยงาน
              </FormLabel>
              <Select
                name="EstimateUnit"
                value={filterData.EstimateUnit}
                onChange={handleInputChange}
                placeholder="เลือกหน่วยงาน (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {unitOptions.map((unit) => (
                  <option key={unit.UnitID} value={unit.UnitName}>
                    {unit.UnitName}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                ประเภทปศุสัตว์
              </FormLabel>
              <Select
                name="LivestockType"
                value={filterData.LivestockType}
                onChange={handleInputChange}
                placeholder="เลือกประเภทปศุสัตว์ (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {livestockTypeOptions.map((type) => (
                  <option
                    key={type.LivestockTypeID}
                    value={type.LivestockTypeID}
                  >
                    {type.LivestockTypeName}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วัคซีน
              </FormLabel>
              <Select
                name="VaccineNameTH"
                value={filterData.VaccineNameTH}
                onChange={handleInputChange}
                placeholder="เลือกวัคซีน (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {vaccineOptions.map((vaccine) => (
                  <option
                    key={vaccine.VaccineID}
                    value={vaccine.VaccineNameTH}
                  >
                    {vaccine.VaccineNameTH}
                  </option>
                ))}
              </Select>
            </FormControl>
          </HStack>
          <Box w="100%" textAlign="right">
            <HStack spacing={5} justifyContent="flex-end">
              <Clear
                onClick={() =>
                  setFilterData({
                    EstimateYear: "",
                    EstimateUnit: "",
                    LivestockType: "",
                    VaccineNameTH: "",
                  })
                }
              />
              <Search onClick={fetchDemandPlanReport} isLoading={isLoading} />
            </HStack>
          </Box>
        </VStack>
      </Box>

      {/* ตาราง */}
      {demandData.length > 0 && (
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <Box
            mb={5}
            borderRadius="full"
            w="100%"
            bgColor="iOrange.500"
            color="white"
            p={2}
          >
            <HStack spacing={5} ml={5}>
              <FontAwesomeIcon icon={faTable} color="white" size="xl" />
              <Heading size={{ base: "md", md: "lg" }} fontWeight="normal">
                รายการแผนความต้องการวัคซีน
              </Heading>
            </HStack>
          </Box>
          <HStack spacing={5} m={5}>
            <Spacer />
            <ExportExcel onClick={exportToExcel} />
            <ExportPdf onClick={exportToPDF} />
            <Print onClick={printTable} />
          </HStack>
          <Box p={5} overflowX="auto">
            <DataTable
              columns={columns}
              data={demandData}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        </Box>
      )}

      {/* กราฟ */}
      {demandData.length > 0 && (
        <VaccinePreventionDemandPlanChart
          data={demandData}
          unitOptions={unitOptions}
        />
      )}
    </Box>
  );
};

export default VaccinePreventionDemandPlanReport;